import axiosInstance from 'api/axios'
import { AxiosResponse } from 'axios'
import {
  CancelSubscriptionResponse,
  PayNowResponse,
  GetPaymentHistoryRequest,
  GetPaymentHistoryResponse,
  SubscribeResponse,
  SubscriptionPlanType,
  UserBillingProfile,
  UserSubscription,
} from 'types'
import { finalizeSubscriptionPaymentAnonymous } from 'api/auth'

export const getPaymentHistory = async (
  request: GetPaymentHistoryRequest,
): Promise<AxiosResponse<GetPaymentHistoryResponse>> => {
  return axiosInstance.post('/users/payment-history', request)
}

export const subscribeExistingUser = (
  paymentMethodId: string,
  plan: SubscriptionPlanType,
): Promise<{ data: SubscribeResponse }> => {
  return axiosInstance.post('/user-subscriptions/create', { paymentMethodId, plan })
}

export interface SetupBillingProfileResponse {
  billingProfileId: number
  stripeCustomerId: string
  setupIntentId: string
  clientSecret: string
}

export const setupBillingProfile = (): Promise<{ data: SetupBillingProfileResponse }> => {
  return axiosInstance.post('/user-billing-profiles/setup-intent')
}

export const finalizeBillingProfile = (
  setupIntentId: string,
  setAsDefault = true,
): Promise<{ data: UserBillingProfile }> => {
  return axiosInstance.post('/user-billing-profiles/finalize', { setupIntentId, setAsDefault })
}

export const getBillingProfiles = (): Promise<{ data: UserBillingProfile[] }> => {
  return axiosInstance.post('/user-billing-profiles', {})
}

export const syncSubscription = (id: number): Promise<{ data: UserSubscription }> => {
  return axiosInstance.post('/user-subscriptions/sync', { id })
}

export interface SyncByStripeIdResponse {
  status: string
  source: 'group' | 'individual'
}

export const syncSubscriptionByStripeId = (
  stripeSubscriptionId: string,
): Promise<{ data: SyncByStripeIdResponse }> => {
  return axiosInstance.post('/user-subscriptions/sync', { stripeSubscriptionId })
}

/**
 * Unified finalize step that must follow every successful Stripe payment (including 3DS).
 *
 *  - signup (anonymous, email passed) + stripeSubscriptionId → /auth/finalize-subscription-payment
 *  - logged-in + stripeSubscriptionId → /user-subscriptions/sync { stripeSubscriptionId }
 *  - logged-in individual without stripeSubscriptionId → /user-subscriptions/sync { id }
 *
 * Caller should `refreshUser()` afterwards.
 */
export const finalizeSubscriptionAfterPayment = async (
  response: SubscribeResponse,
  email?: string,
): Promise<void> => {
  if (email && response.stripeSubscriptionId) {
    await finalizeSubscriptionPaymentAnonymous(email, response.stripeSubscriptionId)
    return
  }
  if (response.stripeSubscriptionId) {
    await syncSubscriptionByStripeId(response.stripeSubscriptionId)
    return
  }
  if (response.userSubscriptionId) {
    await syncSubscription(response.userSubscriptionId)
  }
}

export const payNow = (id: number, subscriptionId?: number): Promise<{ data: PayNowResponse }> => {
  return axiosInstance.post('/user-billing-profiles/pay-now', { id, subscriptionId })
}

/**
 * Owner of a paid Individual or Family subscription turns auto-renewal off. The backend refuses
 * partner-funded plans, Family members and repeated calls — callers hide the action in those cases.
 */
export const cancelSubscription = (): Promise<{ data: CancelSubscriptionResponse }> => {
  return axiosInstance.post('/user-subscriptions/cancel', {})
}

export const deleteBillingProfile = (id: number): Promise<void> => {
  return axiosInstance.post('/user-billing-profiles/delete', { id })
}

export const changeSubscriptionPlan = (
  id: number,
  plan: SubscriptionPlanType,
): Promise<{ data: SubscribeResponse }> => {
  return axiosInstance.post('/user-subscriptions/change', { id, plan })
}
